/**
 * pSeoPageLoader.js
 *
 * Loads the 500+ pSEO pages from the JSON manifest and hydrates them
 * into the same page-object shape that SeoLandingPage expects.
 *
 * Image keys in the JSON are resolved to actual imported image objects
 * from seoPageData.js's shared pools.
 */

import pSeoPagesRaw from "../../data/pSeoPages.json";

import {
  SECTION_LABELS,
  defaultCta,
  heroIllustrations,
  heroScreenshots,
  buildStructuredData,
  createBreadcrumbs,
} from "./seoPageData";

/* ---------- hydrate one JSON entry into a full page object ---------- */

const hydratePage = (raw) => {
  const primaryImage =
    heroIllustrations[raw.primaryImageKey] || heroIllustrations.innovation;
  const secondaryImage =
    heroScreenshots[raw.secondaryImageKey] || heroScreenshots.dashboard;

  const sectionLabels = SECTION_LABELS[raw.type] || SECTION_LABELS.commercial;

  const page = {
    ...raw,
    primaryImage,
    secondaryImage,
    sectionLabels,
    cta: raw.cta || defaultCta,
    breadcrumbs: createBreadcrumbs(raw.path, raw.title),
    relatedLinks: (raw.relatedPaths || []).map((p) => ({
      to: p,
      title: p.replace(/^\//, "").replace(/-/g, " ").replace(/\//g, " › "),
      description: "",
    })),
  };

  page.structuredData = buildStructuredData(page);
  return page;
};

/* ---------- build maps and routes ---------- */

export const pSeoPages = pSeoPagesRaw.map(hydratePage);

export const pSeoPageMap = Object.fromEntries(
  pSeoPages.map((page) => [page.key, page]),
);

export const pSeoPagePathMap = Object.fromEntries(
  pSeoPages.map((page) => [page.path, page]),
);

export const pSeoPageRoutes = pSeoPages.map((page) => ({
  key: page.key,
  path: page.path,
}));

/* ---------- SEO config lookup (used by SeoRouteManager) ---------- */

export const getPSeoConfigForPath = (pathname) => {
  const page = pSeoPagePathMap[pathname];
  if (!page) {
    return null;
  }

  return {
    title: page.seoTitle,
    description: page.seoDescription,
    canonicalPath: page.path,
    ogType: page.type === "guide" ? "article" : "website",
    structuredData: page.structuredData,
    alternatePaths: null, // pSEO pages are English-only for now
  };
};
